<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  // External library imports
  import { useI18n } from 'vue-i18n'

  import CPDivider from '@ui/components/CPDivider.vue'
  import CPSpin from '@ui/components/CPSpin.vue'
  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import { createElementId } from '@common/core-layer/utils/core.utils'

  import { getConfig } from '../../../core-layer/config'
  import { useAuth } from '../../composables/useAuth'
  import { AuthProvider, LoginEvents, Screens } from '../../types/auth'
  import { buildSetPasswordScreenChange, isDeferredInPlacePasswordLoginResult, shouldShowSetPasswordAfterLoginResponse } from '../../utils'

  // Type imports

  // Props definitions
  const props = defineProps({
    loginType: {
      type: String,
      required: true,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    /** Clientportal opt-in — defer session until after in-place Set Password. */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
    (e: 'updateLoader', value: boolean): void
  }>()

  // Composables
  const { t } = useI18n()

  // Refs and reactive variables
  const isLoading = ref(true)
  /**
   * Browser `origin` only (avoids proxy hop mismatch from request URL). Empty on SSR.
   * The iframe is rendered inside `<ClientOnly>` so it never mounts with an empty
   * origin — see the bug where SSR HTML carried `?origin=` and the iframe loaded
   * its first navigation against an empty origin before hydration could repair it.
   */
  const pageOriginForSso = ref(import.meta.client ? (globalThis.location?.origin ?? '') : '')
  const ssoApp = computed(() => `${getConfig().CLIENT_PORTAL_SSO_URL || ''}/login?origin=${pageOriginForSso.value}`)

  // User-defined functions
  const handleMessage = async function (event: MessageEvent) {
    if (event.origin === (getConfig().CLIENT_PORTAL_SSO_URL || '') && event?.data?.credential) {
      emit('updateLoader', true)
      const dataFromPopup = event?.data?.credential
      try {
        const result = await useAuth().loginWithGoogle(dataFromPopup, {
          deferSessionUntilInPlacePasswordSet: props.deferSetPasswordSession,
        })
        const data = result.data
        if (!data) return

        if (isDeferredInPlacePasswordLoginResult(props.deferSetPasswordSession, result) && data.token) {
          emit('changeScreen', buildSetPasswordScreenChange(data.email ?? '', data))
          return
        }

        if (data.multipleUsers) {
          emit('changeScreen', {
            email: data?.email,
            authProvider: AuthProvider.GOOGLE,
            idToken: dataFromPopup,
            page: Screens.SELECT_USER_MULTI_USER,
            multiUser: data.multipleUsers,
          })
        } else if (data.token) {
          if (shouldShowSetPasswordAfterLoginResponse(data)) {
            emit('changeScreen', buildSetPasswordScreenChange(data.email ?? '', data))
          } else {
            emit(LoginEvents.LOGIN_CALLBACK, {
              eventType: LoginEvents.LOGIN_SUCCESSFUL,
              ...data,
            })
          }
        }
      } finally {
        emit('updateLoader', false)
      }
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    if (import.meta.client && globalThis.location?.origin) {
      pageOriginForSso.value = globalThis.location.origin
    }
    window.addEventListener('message', handleMessage)
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
  })
</script>

<template>
  <div class="w-full">
    <ClientOnly>
      <div
        :id="createElementId('google-one-tap', '1', 'container')"
        class="flex items-center justify-center h-[3.125rem] mt-6 -mx-4"
        style="width: calc(100% + 2rem)"
        :class="isLoading ? 'hidden' : 'block'"
      >
        <iframe
          :id="createElementId('google-one-tap', '1', 'iframe')"
          :src="ssoApp"
          width="100%"
          style="border: none; height: 2.75rem"
          allow="identity-credentials-get"
          :title="t('accessibility.continueWithGoogle')"
          @load="() => (isLoading = false)"
        ></iframe>
      </div>
    </ClientOnly>
    <div class="flex w-full items-center justify-center">
      <div
        v-if="isLoading"
        class="mt-6 flex h-[2.5rem] w-full cursor-pointer items-center justify-center rounded-md border border-solid border-default px-0 text-body hover:bg-primary-hover"
      >
        <CPSpin id="google-signin-spinner" size="sm" />
      </div>
    </div>

    <CPDivider>
      <CPText :id="createElementId('login-modal', 'with-email', 'divider')" size="lg" weight="medium" class="text-600">
        {{ loginType === 'SIGN_IN' ? t('auth.orLoginWithYourEmail') : t('auth.orSignUpWithYourEmail') }}
      </CPText>
    </CPDivider>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 12px 0;
  }

  .custom-google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 44px;
  }

  .google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .custom-google-button iframe {
    height: 44px !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .form-item {
    height: 62px !important;
  }
</style>
